import { Button } from "@/components/ui/button";
import { ProjectCard } from "@/components/workspace/project-card";
import type { WorkspaceFolder, WorkspaceProject } from "@/components/workspace/workspace-types";
import { useWorkspaceData } from "@/hooks/use-workspace-data";
/**
 * Gallery view — the responsive grid of project cards + folder chips.
 *
 * "Empty" and "error" states live alongside the main render path so a
 * brand-new workspace doesn't look broken.
 */
import { Folder } from "lucide-react";

export interface WorkspaceGalleryViewProps {
  projects: WorkspaceProject[];
  folders: WorkspaceFolder[];
  onSelectFolder: (folderId: string) => void;
  onCreateProject: () => void;
  onCreateFolder: () => void;
}

export function WorkspaceGalleryView({
  projects,
  folders,
  onSelectFolder,
  onCreateProject,
  onCreateFolder,
}: WorkspaceGalleryViewProps): JSX.Element {
  const flags = useWorkspaceData((s) => s.flags);

  if (folders.length === 0 && projects.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-border bg-muted/30 p-12 text-center">
        <div className="rounded-full bg-muted p-3">
          <Folder className="h-6 w-6 text-muted-foreground" />
        </div>
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">No projects yet</h2>
          <p className="text-sm text-muted-foreground">
            Create a project to start working on a PCB, or add a folder to organize them.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button onClick={onCreateProject} disabled={!flags.projects_writable}>
            New project
          </Button>
          <Button onClick={onCreateFolder} variant="outline" disabled={!flags.folders_writable}>
            New folder
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Folder and project creation land when the workspace API is wired to D1.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {folders.length > 0 ? (
        <section>
          <h2 className="mb-3 text-sm font-medium uppercase tracking-wider text-muted-foreground">
            Folders
          </h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {folders.map((folder) => (
              <button
                type="button"
                key={folder.id}
                onClick={() => onSelectFolder(folder.id)}
                className="flex items-center gap-3 rounded-lg border border-border bg-card p-3 text-left transition-colors hover:border-primary/40 hover:bg-accent/40"
              >
                <div className="rounded-md bg-primary/10 p-2 text-primary">
                  <Folder className="h-4 w-4" />
                </div>
                <div className="flex-1 truncate">
                  <div className="truncate text-sm font-medium">{folder.name}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {folder.project_count} project{folder.project_count === 1 ? "" : "s"}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>
      ) : null}

      {projects.length > 0 ? (
        <section>
          <h2 className="mb-3 text-sm font-medium uppercase tracking-wider text-muted-foreground">
            Projects
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {projects.map((project) => (
              <ProjectCard key={project.id} project={project} view="gallery" />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
